import React, { useState, useEffect, useContext, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPrice } from "../../utils/plans";
import AdminPerPropertyPlan from "./AdminPerPropertyPlan";

const ALL_FEATURES = [
  "basic_dashboard", "payments", "maintenance", "whatsapp", "leases",
  "service_persons", "agreements", "commercial", "reports", "csv_export",
  "analytics", "automation", "multi_user", "priority_support"
];
const FEATURE_LABELS = {
  basic_dashboard: "Dashboard", payments: "Rent Payments", maintenance: "Maintenance",
  whatsapp: "WhatsApp", leases: "Leases", service_persons: "Service Persons",
  agreements: "Agreements", commercial: "Commercial", reports: "Reports",
  csv_export: "CSV Export", analytics: "Analytics", automation: "Automation",
  multi_user: "Multi-User", priority_support: "Priority Support"
};

export default function AdminPricing() {
  const { h, go, flash } = useContext(Ctx);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan id or "new"
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Free-trial config — toggle controls whether brand-new landlord
  // signups get N days of a paid plan before downgrading to Free.
  // Stored as a singleton row in `settings` (org_id='global').
  const [trial, setTrial] = useState({ enabled: false, duration_days: 30, plan_id: 'pro' });
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialSavedFlash, setTrialSavedFlash] = useState(false);

  // Grace-period config — days a paid plan stays in "expired but
  // still paid features" mode before the cron flips it to Free.
  // Hardcoded to 7 until now; super admin can dial it 0–30.
  const [grace, setGrace] = useState({ days: 7 });
  const [graceSaving, setGraceSaving] = useState(false);
  const [graceSavedFlash, setGraceSavedFlash] = useState(false);

  // Master subscriptions switch. When OFF, all plan gates open up
  // (full features, no property cap, no expiry). The setting is read
  // server-side by sync.js, planGuard, and the expiry cron — so
  // flipping this toggle is sufficient on its own; no per-screen UI
  // change is needed elsewhere.
  const [subs, setSubs] = useState({ enabled: true });
  const [subsSaving, setSubsSaving] = useState(false);
  const [subsSavedFlash, setSubsSavedFlash] = useState(false);

  // Pricing mode: which model is currently LIVE (enforced server-side).
  // Mutually exclusive — exactly one is active at a time. Derived from
  // state (plans.is_active + per_property_plans.enabled) so it can
  // never drift.
  const [pricingMode, setPricingMode] = useState('fixed_tier');
  const [pmSaving, setPmSaving] = useState(false);
  const [pmConfirm, setPmConfirm] = useState(null); // pending mode awaiting confirm

  // Pricing mode VIEW: which catalog the admin is currently *looking*
  // at. Independent of pricingMode — the admin can preview the
  // non-live catalog without switching billing. Switching billing
  // happens via the explicit "Switch to this model" button shown
  // inside the inactive panel.
  const [viewMode, setViewMode] = useState('fixed_tier');
  // Tracks whether load() has run once — used to auto-seed viewMode
  // from the live pricing mode on first load only, so subsequent
  // reloads don't yank the user back when they're previewing the
  // non-live catalog.
  const loadedRef = useRef(false);

  const load = async () => {
    const [plansRes, trialRes, graceRes, subsRes, pmRes] = await Promise.all([
      api.adminGetPlans(),
      api.adminGetTrialConfig(),
      api.adminGetGraceConfig(),
      api.adminGetSubscriptionsConfig(),
      api.adminGetPricingMode(),
    ]);
    if (plansRes.success) setPlans(plansRes.plans);
    if (trialRes.success && trialRes.config) setTrial(trialRes.config);
    if (graceRes && graceRes.success && graceRes.config) setGrace(graceRes.config);
    if (subsRes && subsRes.success && subsRes.config) setSubs(subsRes.config);
    if (pmRes && pmRes.success && pmRes.mode) {
      setPricingMode(pmRes.mode);
      // On initial load (or after a switch), default the view to
      // whatever's live so the admin lands on the active catalog.
      // We only auto-set on first load to avoid yanking the user
      // back when they're previewing the other catalog.
      setViewMode(prev => prev || pmRes.mode);
      if (!loadedRef.current) {
        loadedRef.current = true;
        setViewMode(pmRes.mode);
      }
    }
    setLoading(false);
  };

  // Confirmed pricing-mode flip. Posts the new mode to the server,
  // which atomically toggles is_active / enabled flags so exactly
  // one model is live afterward. Reloads the plans list so the
  // is_active changes show in the UI immediately.
  const commitPricingMode = async (mode) => {
    setPmSaving(true);
    const res = await api.adminSetPricingMode(mode);
    setPmSaving(false);
    if (res.success) {
      setPricingMode(res.mode);
      flash?.(mode === 'per_property'
        ? "Switched to Per-Property pricing"
        : "Switched to Fixed-Tier plans");
      setPmConfirm(null);
      await load();
    } else {
      // Surface the server's actual message + detail when present —
      // the generic fallback hides whether it's a missing migration,
      // a bad request, or something else. Useful debugging signal.
      const errMsg = res.message
        ? (res.detail ? `${res.message} (${res.detail})` : res.message)
        : "Failed to switch pricing mode";
      flash?.(errMsg, "error");
      setPmConfirm(null);
    }
  };

  const saveSubs = async (next) => {
    setSubsSaving(true);
    const res = await api.adminSetSubscriptionsConfig(next);
    setSubsSaving(false);
    if (res && res.success) {
      setSubs(res.config || next);
      setSubsSavedFlash(true);
      flash?.(next.enabled
        ? "Subscriptions ENABLED — plan limits now apply"
        : "Subscriptions DISABLED — all landlords have full access");
      setTimeout(() => setSubsSavedFlash(false), 1800);
    } else {
      flash?.((res && res.message) || "Failed to save subscriptions toggle", "error");
    }
  };

  useEffect(() => { load(); }, []);

  const saveGrace = async (next) => {
    setGraceSaving(true);
    const res = await api.adminSetGraceConfig(next);
    setGraceSaving(false);
    if (res && res.success) {
      setGrace(res.config || next);
      setGraceSavedFlash(true);
      flash?.("Grace period saved");
      setTimeout(() => setGraceSavedFlash(false), 1800);
    } else {
      flash?.((res && res.message) || "Failed to save grace period", "error");
    }
  };

  const saveTrial = async (next) => {
    setTrialSaving(true);
    const res = await api.adminSetTrialConfig(next);
    setTrialSaving(false);
    if (res.success) {
      setTrial(res.config || next);
      setTrialSavedFlash(true);
      flash?.("Trial config saved");
      setTimeout(() => setTrialSavedFlash(false), 1800);
    } else {
      flash?.(res.message || "Failed to save trial config", "error");
    }
  };

  const startEdit = (plan) => {
    setEditing(plan.id);
    setForm({ ...plan, features: plan.features || [] });
  };

  const startNew = () => {
    setEditing("new");
    setForm({ id: "", name: "", price: 0, billing_cycle: "monthly", max_properties: 3, max_units: 5, max_tenants: 5, features: ["basic_dashboard", "payments"], is_active: 1, sort_order: plans.length });
  };

  const toggleFeature = (f) => {
    const features = form.features || [];
    setForm(prev => ({
      ...prev,
      features: features.includes(f) ? features.filter(x => x !== f) : [...features, f]
    }));
  };

  const handleSave = async () => {
    if (!form.name) { flash("Name required", "error"); return; }
    setSaving(true);
    let res;
    if (editing === "new") {
      if (!form.id) { flash("ID required", "error"); setSaving(false); return; }
      res = await api.adminCreatePlan(form);
    } else {
      res = await api.adminUpdatePlan(editing, form);
    }
    if (res.success) {
      flash(editing === "new" ? "Plan created!" : "Plan updated!");
      setEditing(null);
      await load();
    } else {
      flash(res.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete plan "${id}"?`)) return;
    const res = await api.adminDeletePlan(id);
    if (res.success) {
      flash(res.message || "Plan deleted");
      await load();
    } else {
      flash(res.message || "Failed to delete", "error");
    }
  };

  const fieldS = {
    width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm,
    border: `1px solid ${h.bd}`, background: h.ib, color: h.tx,
    ...type.body, outline: "none", boxSizing: "border-box",
  };

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>;
  }

  // Edit/Create form
  if (editing) {
    return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
      <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back to Plans
      </button>
      <h2 style={{ ...type.h2, color: h.tx, margin: `0 0 ${sp.lg}px` }}>
        {editing === "new" ? "Create Plan" : `Edit: ${form.name}`}
      </h2>

      <div style={{ ...crd(h) }}>
        {editing === "new" && <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Plan ID (lowercase, no spaces)</label>
          <input value={form.id || ""} onChange={e => setForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} style={fieldS} placeholder="e.g. starter" />
        </div>}

        <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Plan Name</label>
          <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={fieldS} placeholder="e.g. Starter" />
        </div>

        {/* Inputs are in rupees for usability — typing 299 is less
            error-prone than typing 29900. Storage is still paise (×100)
            so Razorpay calls and existing rows stay correct. */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Monthly price (₹)</label>
            <input
              type="number"
              value={form.price ? form.price / 100 : 0}
              onChange={e => {
                const rupees = parseFloat(e.target.value) || 0;
                setForm(f => ({ ...f, price: Math.round(rupees * 100) }));
              }}
              style={fieldS}
            />
            <div style={{ ...type.xs, color: h.tt, marginTop: sp.xs }}>= {formatPrice(form.price)}/mo</div>
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Sort Order</label>
            <input type="number" value={form.sort_order || 0} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
        </div>

        {/* Yearly pricing — admin sets this independently of monthly so
            a 12-month commit can carry a real discount. Leave blank /
            zero to disable the yearly option for this plan. */}
        <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Yearly price (₹) — optional</label>
          <input
            type="number"
            value={form.price_yearly ? form.price_yearly / 100 : ''}
            onChange={e => {
              if (e.target.value === '') {
                setForm(f => ({ ...f, price_yearly: null }));
                return;
              }
              const rupees = parseFloat(e.target.value) || 0;
              setForm(f => ({ ...f, price_yearly: Math.round(rupees * 100) }));
            }}
            style={fieldS}
            placeholder="Leave blank to hide yearly option"
          />
          {(() => {
            const yearly = form.price_yearly || 0;
            const monthly = form.price || 0;
            if (!yearly) return <div style={{ ...type.xs, color: h.tt, marginTop: sp.xs }}>No yearly option will be shown for this plan.</div>;
            const fullYear = monthly * 12;
            const savings = fullYear > 0 ? Math.round((1 - yearly / fullYear) * 100) : 0;
            const effMonthly = Math.round(yearly / 12);
            return <div style={{ ...type.xs, color: h.tt, marginTop: sp.xs, lineHeight: 1.5 }}>
              = {formatPrice(yearly)}/yr
              {monthly > 0 && <> · effective {formatPrice(effMonthly)}/mo</>}
              {savings > 0 && <> · <span style={{ color: h.gn, fontWeight: 700 }}>{savings}% off vs monthly × 12</span></>}
              {savings < 0 && <> · <span style={{ color: h.rd, fontWeight: 700 }}>{Math.abs(savings)}% MORE than monthly × 12</span></>}
            </div>;
          })()}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Properties</label>
            <input type="number" value={form.max_properties || 0} onChange={e => setForm(f => ({ ...f, max_properties: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Units</label>
            <input type="number" value={form.max_units || 0} onChange={e => setForm(f => ({ ...f, max_units: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Tenants</label>
            <input type="number" value={form.max_tenants || 0} onChange={e => setForm(f => ({ ...f, max_tenants: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
        </div>

        <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.sm }}>Features</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.xs }}>
            {ALL_FEATURES.map(f => {
              const on = (form.features || []).includes(f);
              return <div key={f} onClick={() => toggleFeature(f)} style={{
                display: "flex", alignItems: "center", gap: sp.sm, padding: `${sp.xs}px ${sp.sm}px`,
                borderRadius: rad.sm, border: `1px solid ${on ? h.ac : h.bd}`,
                background: on ? `${h.ac}08` : "transparent", cursor: "pointer"
              }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${on ? h.ac : h.bd}`, background: on ? h.ac : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {on && <span style={{ color: "#fff", fontSize: 10, fontWeight: 700 }}>{"\u2713"}</span>}
                </div>
                <span style={{ ...type.xs, color: h.tx }}>{FEATURE_LABELS[f] || f}</span>
              </div>;
            })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts }}>Active</label>
          <div onClick={() => setForm(f => ({ ...f, is_active: f.is_active ? 0 : 1 }))} style={{
            width: 44, height: 24, borderRadius: 12, background: form.is_active ? h.gn : h.bd,
            cursor: "pointer", position: "relative", transition: "background 0.2s"
          }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", position: "absolute", top: 2, left: form.is_active ? 22 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{
            width: "100%", padding: sp.md, borderRadius: rad.md, border: "none",
            background: h.ac, color: "#fff", ...type.sm, fontWeight: 600,
            cursor: "pointer", opacity: saving ? 0.6 : 1
          }}
        >{saving ? "Saving..." : editing === "new" ? "Create Plan" : "Save Changes"}</button>
      </div>
    </div>;
  }

  // Plans list view
  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Dashboard
    </button>

    {/* Header is now title-only. The Add Plan action moved down to
        sit directly above the Fixed-Tier catalog (where it acts) so
        it's contextual instead of floating in the page header. */}
    <h2 style={{ ...type.h2, color: h.tx, margin: `0 0 ${sp.lg}px` }}>Pricing & Plans</h2>

    {/* Subscriptions ON/OFF master switch. Loud styling when OFF
        because it's a significant runtime state — every landlord
        gets full features for free until it's flipped back. */}
    <div style={{
      ...crd(h),
      marginBottom: sp.md,
      position: "relative",
      borderLeft: `4px solid ${subs.enabled ? h.gn : h.wn}`,
      background: subs.enabled ? h.cd : `${h.wn}08`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={icBox(subs.enabled ? h.gn : h.wn, 40)}>
          <span style={{ fontSize: 18 }}>{subs.enabled ? "💳" : "🆓"}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
            Subscriptions {subs.enabled ? "Enabled" : "Disabled"}
          </div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2, lineHeight: 1.4 }}>
            {subs.enabled
              ? "Plans, property limits, feature gates, and the expiry cron are all active."
              : "Master switch is OFF — every landlord has full features, unlimited properties, no expiry."}
          </div>
        </div>
        <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: subsSaving ? "wait" : "pointer", flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={!!subs.enabled}
            disabled={subsSaving}
            onChange={(e) => saveSubs({ ...subs, enabled: e.target.checked })}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: "absolute", inset: 0,
            background: subs.enabled ? h.gn : h.wn,
            borderRadius: 24,
            transition: "0.2s",
          }} />
          <span style={{
            position: "absolute", top: 2, left: subs.enabled ? 22 : 2,
            width: 20, height: 20, background: "#fff", borderRadius: "50%",
            transition: "0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }} />
        </label>
      </div>
      {!subs.enabled && (
        <div style={{
          marginTop: sp.sm, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}`,
          ...type.xs, color: h.wn, fontWeight: 600, lineHeight: 1.4,
        }}>
          ⚠️ The plans below are not being enforced. New signups, paid renewals, the auto-downgrade cron, and feature gates are all paused until you flip this back ON.
        </div>
      )}
      {subsSavedFlash && (
        <div style={{ ...type.xs, color: h.gn, fontWeight: 600, marginTop: sp.xs }}>✓ Saved</div>
      )}
    </div>

    {/* Pricing Mode segmented toggle — mutually exclusive between
        the fixed-tier ladder and per-property unit pricing. The
        server enforces single-mode-live; this control just sends
        the intent and reloads. Only meaningful when the master
        subscriptions switch above is ON. */}
    <div style={{
      ...crd(h),
      marginBottom: sp.md,
      opacity: subs.enabled ? 1 : 0.55,
      transition: "opacity 0.2s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
        <div style={icBox(h.ac, 40)}>
          <span style={{ fontSize: 18 }}>⚖️</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>Pricing Model</div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2, lineHeight: 1.4 }}>
            Only one pricing model can be live at a time. Switching auto-deactivates the other.
          </div>
        </div>
      </div>

      {/* Segmented control — each option carries its own semantic
          color so the live mode is identifiable at a glance.
          Fixed-Tier = brand-blue (legacy/stable connotation),
          Per-Property = teal-green (growth/scaling connotation).
          Active state uses a gradient-tinted background + colored
          border + bold icon; inactive is muted with low-opacity
          color hint that previews what activation looks like. */}
      <div style={{
        display: "flex",
        gap: 8,
        marginTop: sp.sm,
      }}>
        {[
          {
            id: 'fixed_tier',
            label: 'Fixed-Tier Plans',
            sub: 'Free / Starter / Growth / Pro',
            color: h.ac || '#3b82f6',
            iconPath: <>
              {/* Layered tiers icon */}
              <polygon points="12 2 2 7 12 12 22 7 12 2" />
              <polyline points="2 17 12 22 22 17" />
              <polyline points="2 12 12 17 22 12" />
            </>,
          },
          {
            id: 'per_property',
            label: 'Per-Property',
            sub: '₹ × properties (with volume tiers)',
            color: h.gn || '#16a34a',
            iconPath: <>
              {/* Building / property icon */}
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4v18" />
              <path d="M19 21V11l-6-4" />
              <path d="M9 9h.01" />
              <path d="M9 13h.01" />
              <path d="M9 17h.01" />
            </>,
          },
        ].map(opt => {
          // Visual "active" = which tab the admin is viewing.
          // Separate concept of "live" = which mode is enforced.
          const active = viewMode === opt.id;
          const isLive = pricingMode === opt.id;
          const cardBg = active
            ? `linear-gradient(135deg, ${opt.color}18 0%, ${opt.color}08 100%)`
            : "transparent";
          const cardBorder = active ? opt.color : (h.bd || "rgba(0,0,0,0.1)");
          return <button
            key={opt.id}
            onClick={() => {
              // Pure view switch — no confirmation. The actual mode
              // change happens via the in-panel "Switch to this model"
              // button which still triggers the confirmation flow.
              if (active || pmSaving) return;
              setViewMode(opt.id);
            }}
            disabled={pmSaving}
            style={{
              flex: 1,
              padding: `${sp.md}px ${sp.sm + 2}px`,
              borderRadius: rad.md || 12,
              border: `1.5px solid ${cardBorder}`,
              background: cardBg,
              color: active ? h.tx : h.ts,
              fontWeight: active ? 700 : 600,
              fontSize: 12,
              cursor: (pmSaving || !subs.enabled) ? "not-allowed" : (active ? "default" : "pointer"),
              boxShadow: active ? `0 4px 14px ${opt.color}20` : "none",
              transition: "all 0.2s ease",
              textAlign: "left",
              lineHeight: 1.3,
              position: "relative",
            }}>
            {/* "LIVE" pill in the top-right — shows on whichever mode
                is currently enforced server-side, regardless of which
                tab the admin is viewing. Separates the concept of
                "which catalog am I looking at" from "which is the
                billing model right now". */}
            {isLive && (
              <span style={{
                position: "absolute",
                top: 8, right: 8,
                background: opt.color,
                color: "#fff",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: "2px 8px",
                borderRadius: 999,
                boxShadow: `0 2px 6px ${opt.color}40`,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}>
                <span style={{
                  width: 5, height: 5,
                  borderRadius: "50%",
                  background: "#fff",
                  display: "inline-block",
                }} />
                LIVE
              </span>
            )}

            {/* Icon + Label row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: 8,
                background: active ? opt.color : `${opt.color}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s ease",
                flexShrink: 0,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke={active ? "#fff" : opt.color}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true">
                  {opt.iconPath}
                </svg>
              </div>
              <span style={{
                color: active ? opt.color : h.tx,
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: "-0.1px",
              }}>{opt.label}</span>
            </div>

            <div style={{
              ...type.xs,
              color: active ? h.ts : h.tt || h.sc,
              fontWeight: 500,
              fontSize: 10.5,
              lineHeight: 1.4,
              opacity: active ? 1 : 0.85,
            }}>{opt.sub}</div>
          </button>;
        })}
      </div>

      {!subs.enabled && (
        <div style={{ ...type.xs, color: h.wn, fontWeight: 600, marginTop: sp.sm, lineHeight: 1.4 }}>
          Master subscriptions toggle is OFF — pricing mode has no effect until it's re-enabled.
        </div>
      )}

      {/* Confirmation card — explains what flipping the mode does,
          so the admin doesn't trigger it by accident. */}
      {pmConfirm && (
        <div style={{
          marginTop: sp.sm,
          paddingTop: sp.sm,
          borderTop: `1px dashed ${h.bd}`,
        }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700, marginBottom: sp.xs }}>
            Switch to {pmConfirm === 'per_property' ? 'Per-Property' : 'Fixed-Tier'}?
          </div>
          <div style={{ ...type.xs, color: h.sc, marginBottom: sp.sm, lineHeight: 1.5 }}>
            {pmConfirm === 'per_property' ? (
              <>
                <strong style={{ color: h.tx }}>Activates:</strong> Per-Property plan (₹ × property count)<br />
                <strong style={{ color: h.tx }}>Deactivates:</strong> Starter, Growth, Pro<br />
                <span style={{ color: h.tt }}>Existing customers on Starter/Growth/Pro keep their plan until their current cycle ends.</span>
              </>
            ) : (
              <>
                <strong style={{ color: h.tx }}>Activates:</strong> Free, Starter, Growth, Pro<br />
                <strong style={{ color: h.tx }}>Deactivates:</strong> Per-Property plan<br />
                <span style={{ color: h.tt }}>Existing customers on the Per-Property plan keep it until their current cycle ends.</span>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: sp.sm }}>
            <button
              onClick={() => setPmConfirm(null)}
              disabled={pmSaving}
              style={{
                flex: 1, padding: sp.sm + 2, borderRadius: rad.sm,
                border: `1px solid ${h.bd}`, background: "transparent",
                color: h.ts, ...type.xs, fontWeight: 600, cursor: "pointer",
              }}>
              Cancel
            </button>
            <button
              onClick={() => commitPricingMode(pmConfirm)}
              disabled={pmSaving}
              style={{
                flex: 2, padding: sp.sm + 2, borderRadius: rad.sm,
                border: "none", background: h.ac, color: "#fff",
                ...type.xs, fontWeight: 700, cursor: "pointer",
                opacity: pmSaving ? 0.6 : 1,
              }}>
              {pmSaving ? "Switching…" : `Switch to ${pmConfirm === 'per_property' ? 'Per-Property' : 'Fixed-Tier'}`}
            </button>
          </div>
        </div>
      )}
    </div>

    {/* "Switch to this model" banner — sits directly under the
        Pricing Model toggle so the discrepancy between what the
        admin is viewing and what's live is immediately visible
        before they scroll past to other settings. Single CTA
        triggers the existing confirm flow. Skipped when the
        master subscriptions toggle is off entirely. */}
    {subs.enabled && viewMode !== pricingMode && (
      <div style={{
        ...crd(h),
        marginBottom: sp.md,
        borderLeft: `4px solid ${h.wn}`,
        background: `${h.wn}06`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
          <div style={icBox(h.wn, 36)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={h.wn} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 9v4M12 17h.01" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
              Not the live pricing model
            </div>
            <div style={{ ...type.xs, color: h.sc, marginTop: 2, lineHeight: 1.4 }}>
              You're viewing the {viewMode === 'per_property' ? 'per-property' : 'fixed-tier'} catalog.{' '}
              <strong style={{ color: h.tx }}>
                {pricingMode === 'per_property' ? 'Per-Property' : 'Fixed-Tier'}
              </strong>{' '}
              is currently live.
            </div>
          </div>
        </div>
        <button
          onClick={() => setPmConfirm(viewMode)}
          disabled={pmSaving}
          style={{
            width: "100%",
            padding: `${sp.sm + 2}px`,
            borderRadius: rad.sm,
            border: `1px solid ${h.wn}`,
            background: "transparent",
            color: h.wn,
            ...type.xs,
            fontWeight: 700,
            cursor: "pointer",
            opacity: pmSaving ? 0.6 : 1,
          }}>
          {pmSaving ? "Switching…" : `Switch to ${viewMode === 'per_property' ? 'Per-Property' : 'Fixed-Tier'} →`}
        </button>
      </div>
    )}

    {/* Free-Trial Toggle — applies to NEW landlord signups only.
        Existing orgs are unaffected. After expiry the existing
        plan-expiry checker downgrades them to Free automatically. */}
    <div style={{ ...crd(h), marginBottom: sp.md, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
        <div style={icBox(h.gn, 40)}>
          <span style={{ fontSize: 18 }}>🎁</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>Free Trial for New Signups</div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2 }}>
            Every new landlord gets full plan features for N days, then auto-downgrades to Free.
          </div>
        </div>
        <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: trialSaving ? "wait" : "pointer", flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={!!trial.enabled}
            disabled={trialSaving}
            onChange={(e) => saveTrial({ ...trial, enabled: e.target.checked })}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: "absolute", inset: 0,
            background: trial.enabled ? h.gn : "#ccc",
            borderRadius: 24,
            transition: "0.2s",
          }} />
          <span style={{
            position: "absolute", top: 2, left: trial.enabled ? 22 : 2,
            width: 20, height: 20, background: "#fff", borderRadius: "50%",
            transition: "0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }} />
        </label>
      </div>

      {trial.enabled && (
        <div style={{ display: "flex", gap: sp.sm, marginTop: sp.sm, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}`, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 110px" }}>
            <label style={{ ...type.xs, color: h.sc, fontWeight: 600, display: "block", marginBottom: 4 }}>Duration (days)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={trial.duration_days}
              onChange={(e) => setTrial({ ...trial, duration_days: parseInt(e.target.value, 10) || 30 })}
              onBlur={() => saveTrial(trial)}
              style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ ...type.xs, color: h.sc, fontWeight: 600, display: "block", marginBottom: 4 }}>Trial Plan</label>
            <select
              value={trial.plan_id}
              onChange={(e) => saveTrial({ ...trial, plan_id: e.target.value })}
              disabled={trialSaving}
              style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13 }}
            >
              {plans
                .filter(p => p.is_active && p.id !== 'free')
                .map(p => <option key={p.id} value={p.id}>{p.name} (₹{formatPrice(p.price)}/mo)</option>)}
              {plans.filter(p => p.is_active && p.id !== 'free').length === 0 && (
                <option value="">— no paid plans configured —</option>
              )}
            </select>
          </div>
          {trialSavedFlash && (
            <div style={{ ...type.xs, color: h.gn, fontWeight: 600, padding: `${sp.sm}px 0` }}>✓ Saved</div>
          )}
        </div>
      )}
    </div>

    {/* Grace-Period config — applies to ALL paid orgs. Days an
        expired plan stays usable before the cron flips it to Free. */}
    <div style={{ ...crd(h), marginBottom: sp.md, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
        <div style={icBox(h.wn, 40)}>
          <span style={{ fontSize: 18 }}>⏳</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>Plan Grace Period</div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2 }}>
            How many days an expired paid plan stays usable before auto-downgrade to Free.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: sp.sm, marginTop: sp.sm, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}`, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 110px" }}>
          <label style={{ ...type.xs, color: h.sc, fontWeight: 600, display: "block", marginBottom: 4 }}>Duration (days)</label>
          <input
            type="number"
            min={0}
            max={30}
            value={grace.days}
            disabled={graceSaving}
            onChange={(e) => setGrace({ days: Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0)) })}
            onBlur={() => saveGrace(grace)}
            style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }}
          />
        </div>
        <div style={{ flex: "1 1 auto", minHeight: 0 }}>
          <div style={{ ...type.xs, color: h.sc, lineHeight: 1.4, paddingBottom: sp.sm }}>
            {grace.days === 0
              ? "Hard cutoff — plan_status flips and downgrade fires the same day."
              : `Landlords see "${grace.days} day${grace.days === 1 ? '' : 's'} left" countdown after plan_end.`}
          </div>
        </div>
      </div>
      {graceSavedFlash && (
        <div style={{ ...type.xs, color: h.gn, fontWeight: 600, padding: `${sp.xs}px 0 0` }}>✓ Saved</div>
      )}
    </div>

    {/* Per-Property catalog view (embedded — host controls chrome) */}
    {viewMode === 'per_property' && <AdminPerPropertyPlan embedded />}

    {/* Fixed-Tier plans catalog */}
    {viewMode === 'fixed_tier' && <>
      {/* Add Plan — sits right above the catalog it acts on so the
          'create new fixed-tier plan' affordance is contextually
          adjacent to the list it modifies. Right-aligned to match
          a section-header style without competing with the cards. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: sp.sm }}>
        <button onClick={startNew} style={{
          padding: `${sp.xs + 2}px ${sp.md}px`, borderRadius: rad.sm,
          border: "none", background: h.ac, color: "#fff",
          ...type.xs, fontWeight: 700, cursor: "pointer",
          display: "flex", alignItems: "center", gap: sp.xs,
          boxShadow: `0 2px 8px ${h.ac}30`,
        }}>
          <Ic n="plus" s={14} c="#fff" /> Add Plan
        </button>
      </div>
    <div style={{ display: "flex", flexDirection: "column", gap: sp.sm }}>
      {plans.map(p => <div key={p.id} style={{ ...crd(h), position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
            <div style={icBox(p.price > 0 ? h.gn : h.wn, 40)}>
              <Ic n={p.price > 0 ? "zap" : "shield"} s={18} c={p.price > 0 ? h.gn : h.wn} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
                <span style={{ ...type.h3, color: h.tx }}>{p.name}</span>
                <span style={bdg(p.is_active ? `${h.gn}20` : `${h.rd}20`, p.is_active ? h.gn : h.rd)}>
                  {p.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div style={{ ...type.xs, color: h.ts, marginTop: 2 }}>
                ID: {p.id} {"\u00B7"} {p.max_properties >= 999999 ? "Unlimited" : p.max_properties} props {"\u00B7"} {(p.features || []).length} features
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ ...type.h2, color: p.price > 0 ? h.gn : h.wn }}>{formatPrice(p.price)}</div>
            <div style={{ ...type.xs, color: h.tt }}>/month</div>
          </div>
        </div>

        {/* Features chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs, marginTop: sp.md }}>
          {(p.features || []).map(f => <span key={f} style={{
            ...bdg(`${h.ac}10`, h.ac), fontSize: 9
          }}>{FEATURE_LABELS[f] || f}</span>)}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: sp.sm, marginTop: sp.md, paddingTop: sp.sm, borderTop: `1px solid ${h.bd}` }}>
          <button onClick={() => startEdit(p)} style={{
            flex: 1, padding: sp.sm, borderRadius: rad.sm, border: `1px solid ${h.ac}`,
            background: "transparent", color: h.ac, ...type.xs, fontWeight: 600, cursor: "pointer"
          }}>Edit</button>
          {p.id !== "free" && <button onClick={() => handleDelete(p.id)} style={{
            padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.rd}30`,
            background: "transparent", color: h.rd, ...type.xs, fontWeight: 600, cursor: "pointer"
          }}>Delete</button>}
        </div>
      </div>)}
    </div>
    </>}
  </div>;
}
